'use client';
import { useMutation } from '@tanstack/react-query';
import { RegisterData, VerifyEmailOtpData } from '@/lib/types/auth.type';
import { authApi } from '@/lib/api/auth';
import { setRegistrationCookies } from '@/app/actions/auth.actions';
import { logger } from '@/utils/logger';

/**
 * Registration Hook
 * Manages the registration flow:
 * 1. Register (email + phone + password)
 * 2. Verify Email OTP
 */
export function useRegister() {
  // Step 1: Register user
  const registerMutation = useMutation({
    mutationFn: (data: RegisterData) => authApi.register(data),
    onSuccess: async (response) => {
      const { email, phone } = response.data.data;

      localStorage.setItem('pending_registration', JSON.stringify({
        email,
        phone,
        emailVerified: false,
        email_otp_sent_at: Date.now(),
      }));

      await setRegistrationCookies({ emailPending: true });

      logger.log('Registration successful, email verification pending');
    },
  });

  // Step 2: Verify Email
  const verifyEmailMutation = useMutation({
    mutationFn: (data: VerifyEmailOtpData) => authApi.verifyEmailOtp(data),
    onSuccess: async () => {
      localStorage.removeItem('pending_registration');
      await setRegistrationCookies({ emailPending: false });
      logger.log('Email verified - Registration complete!');
    },
  });

  // Resend Email OTP
  const resendEmailOtpMutation = useMutation({
    mutationFn: (email: string) => authApi.resendEmailOtp(email),
  });

  return {
    // Mutations
    register: registerMutation.mutate,
    registerAsync: registerMutation.mutateAsync,
    verifyEmail: verifyEmailMutation.mutate,
    verifyEmailAsync: verifyEmailMutation.mutateAsync,
    resendEmailOtp: resendEmailOtpMutation.mutate,

    // Loading states
    isRegistering: registerMutation.isPending,
    isVerifyingEmail: verifyEmailMutation.isPending,
    isResendingEmail: resendEmailOtpMutation.isPending,

    // Response data
    registerData: registerMutation.data,
    verifyEmailData: verifyEmailMutation.data,
  };
}
